import { useCallback, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useLocalization, LocalizationKeys } from '../../../i18n'
import { useSubMenuOptions } from '../../header/userProfile/userProfileDropdownContainer/hooks/useSubMenuOptions'
import { usePortalPopup } from '../../../hooks'
import { useToast } from '../../../hooks/context/useToast'
import { ToastType } from '../../../types/toast'
import styles from './languageDropdown.module.css'
import sharedStyles from '../settingsDropdown.module.css'

export interface LanguageDropdownProps {
  onClose: () => void
  buttonRef: RefObject<HTMLButtonElement | null>
}

const PORTAL_OPTIONS = { placement: 'top' as const, offset: 8 }

export function LanguageDropdown({ onClose, buttonRef }: LanguageDropdownProps) {
  const { t, locale, setLocale } = useLocalization()
  const { languageOptions } = useSubMenuOptions()
  const dropdownPosition = usePortalPopup(true, buttonRef, PORTAL_OPTIONS)
  const { addToast } = useToast()

  const handleLanguageSelect = useCallback((selectedLanguage: number) => {
    setLocale(selectedLanguage)
    // Use the new language for the toast message
    addToast(t(LocalizationKeys.MENU_LANGUAGE_CHANGED, selectedLanguage), ToastType.Warning)
    onClose()
  }, [setLocale, onClose, addToast, t])

  if (!dropdownPosition) {
    return null
  }

  return createPortal(
    <div 
      className={sharedStyles.dropdown} 
      role="menu"
      style={{
        position: 'fixed',
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
        transform: 'translateY(-100%)',
      }}
    >
      <div className={styles.header}>
        <span className={styles.title}>{t(LocalizationKeys.MENU_SELECT_LANGUAGE)}</span>
      </div>
      
      <nav>
        <ul className={styles.options} role="none">
          {languageOptions.map((option) => (
            <li key={option.value} className={styles.option} role="none">
              <button
                type="button"
                onClick={() => handleLanguageSelect(option.value as number)}
                className={styles.optionBtn}
                data-active={locale === option.value}
                role="menuitem"
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      </nav>
    </div>,
    document.body
  )
}
